import { Button, Tag } from '@ads/components-react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  FolderArrowDownIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import type { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import scheduleNotificationIlustration from '@/assets/svg/schedule-notification.svg';
import sendNotificationIlustration from '@/assets/svg/send-notification.svg';
import { Skeleton, Stepper } from '@/shared/ui/atoms';
import { useNotificationAudience } from '../../../hooks/useNotificationAudience';
import { useNotificationMessage } from '../../../hooks/useNotificationMessage';
import { useNotificationReview } from '../../../hooks/useNotificationReview';
import { useNotificationWizard } from '../../../hooks/useNotificationWizard';
import { LoadingModal } from '../../molecules/LoadingModal';
import { NotificationAudienceStep } from '../NotificationAudienceStep';
import { NotificationMessageStep } from '../NotificationMessageStep';
import { NotificationReviewStep } from '../NotificationReviewStep';

const WIZARD_STEPS = [
  { title: 'wizard.steps.audience' as const },
  { title: 'wizard.steps.message' as const },
  { title: 'wizard.steps.review' as const },
];

/**
 * Organism orquestrador do wizard de envio de notificação.
 *
 * Gerencia a navegação entre etapas via `useNotificationWizard`,
 * renderiza o stepper, o conteúdo da etapa atual e o footer de navegação.
 */
export function NotificationWizard() {
  const { t } = useTranslation('notification');
  const {
    step,
    goNext,
    goPrev,
    goToStep,
    sendNotification,
    isSending,
    saveDraft,
    isSavingDraft,
    wizardState,
  } = useNotificationWizard();
  const { selectedStudentCount, isLoadingSummary, selectedClassroomIds, selectedStatuses } =
    useNotificationAudience();
  const { title, body } = useNotificationMessage();
  const { schedule } = useNotificationReview();

  const canProceedFromAudience =
    selectedClassroomIds.length > 0 && selectedStatuses.length > 0 && selectedStudentCount > 0;
  const canProceedFromMessage = title.trim().length > 0 && body.trim().length > 0;
  const canSaveDraftFromMessage = title.trim().length > 0 || body.trim().length > 0;
  const canSend =
    canProceedFromAudience &&
    canProceedFromMessage &&
    (schedule.mode === 'now' || (schedule.mode === 'scheduled' && schedule.scheduledAt !== null));

  const steps = WIZARD_STEPS.map(s => ({ title: t(s.title), description: '' }));

  return (
    <div>
      <div className="flex flex-col bg-ctx-layout-surface rounded-md pt-xs">
        <div className="flex justify-center items-center bg-ctx-layout-surface py-xss border-b-hairline border-ctx-layout-border pb-xs">
          <Stepper
            steps={steps}
            currentStep={step}
            onStepClick={idx => {
              if (idx <= step) goToStep(idx);
            }}
          />
        </div>

        <div>
          {step === 0 && <NotificationAudienceStep />}
          {step === 1 && <NotificationMessageStep />}
          {step === 2 && <NotificationReviewStep />}
        </div>
      </div>

      <div className="flex items-center justify-between rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface mt-mini p-mini shadow-level1">
        {step === 0 ? (
          <div className="flex flex-col gap-quark">
            <span className="ts-subtitle-xxs text-ctx-content-title">
              {t('wizard.footer.selectionSummaryTitle')}
            </span>
            <div className="flex items-center gap-nano">
              {isLoadingSummary ? (
                <Skeleton className="h-xxxs w-[5rem]" />
              ) : (
                <Tag
                  label={t('wizard.footer.studentsTag', { count: selectedStudentCount })}
                  size="sm"
                  custom={{ text: 'text-nowrap' }}
                />
              )}
              <Tag
                label={t('wizard.footer.classroomsTag', { count: selectedClassroomIds.length })}
                size="sm"
                custom={{ text: 'text-nowrap' }}
              />
              <Tag
                label={t('wizard.footer.statusesTag', { count: selectedStatuses.length })}
                size="sm"
                custom={{ text: 'text-nowrap' }}
              />
            </div>
          </div>
        ) : (
          <Button
            hierarchy="secondary"
            size="md"
            leadingIcon={ChevronLeftIcon as ComponentType<{ className?: string }>}
            onClick={goPrev}
          >
            {t('wizard.actions.back')}
          </Button>
        )}

        <div className="flex items-center gap-xs">
          {step > 0 && wizardState.notificationType === 1 && (
            <Button
              hierarchy="secondary"
              size="md"
              leadingIcon={FolderArrowDownIcon as ComponentType<{ className?: string }>}
              disabled={(step === 1 && !canSaveDraftFromMessage) || isSavingDraft}
              isLoading={isSavingDraft}
              onClick={saveDraft}
            >
              {t('wizard.actions.saveDraft')}
            </Button>
          )}
          {step < 2 && (
            <Button
              hierarchy="primary"
              size="md"
              trailingIcon={ChevronRightIcon as ComponentType<{ className?: string }>}
              disabled={
                (step === 0 && !canProceedFromAudience) || (step === 1 && !canProceedFromMessage)
              }
              onClick={goNext}
            >
              {t('wizard.actions.next')}
            </Button>
          )}
          {step === 2 && (
            <Button
              hierarchy="primary"
              size="md"
              trailingIcon={PaperAirplaneIcon as ComponentType<{ className?: string }>}
              disabled={!canSend}
              onClick={sendNotification}
            >
              {t('wizard.actions.send')}
            </Button>
          )}
        </div>
      </div>
      {isSending &&
        (schedule.mode === 'scheduled' ? (
          <LoadingModal
            illustration={scheduleNotificationIlustration}
            title={t('wizard.sending.schedule')}
          />
        ) : (
          <LoadingModal
            illustration={sendNotificationIlustration}
            title={t('wizard.sending.now')}
          />
        ))}
    </div>
  );
}
